import FooterStore from 'store/modules/Footer/Footer'
import store from 'definitions/store'
import CookieUtils from 'utils/CookieUtils'
import axios from 'axios'
import Storyblok from '../storyblok'
import conf from 'conf'

Storyblok.init()

const Init = {
  InitSAP() {
    try {
      const gdprCookies = store.state.main.gdprCookie

      if (!gdprCookies.includes('web') && !gdprCookies.includes('adv')) {
        console.log('SAP init skipped due to missing consent')
        return
      }

      Init.injectSAP()
      Init.injectSapSdk()
    } catch (e) {
      console.warn('SAP init check failed', e)
    }
  },
  injectSAP() {
    try {
      if (typeof document === 'undefined' || document.getElementById('scarab-js-api')) {
        console.log('SAP script already injected')
        return
      }

      window.ScarabQueue = window.ScarabQueue || []

      const js = document.createElement('script')
      js.id = 'scarab-js-api'
      js.src = `https://cdn.scarabresearch.com/js/${conf.sapID}/scarab-v2.js`
      const fs = document.getElementsByTagName('script')[0]
      fs && fs.parentNode && fs.parentNode.insertBefore(js, fs)

      const tag = document.createElement('script')
      tag.id = 'scarab-end'
      tag.type = 'text/javascript'
      tag.innerHTML = `
        if (window.ScarabQueue) {
          window.ScarabQueue.push(['go'])
        } else {
          window.ScarabQueue = [['go']]
        }
      `
      const position = document.querySelector('body')
      position.appendChild(tag)
    } catch (e) {
      console.error('Failed to inject SAP script', e)
    }
  },
  injectSapSdk() {
    try {
      if (typeof document === 'undefined' || document.getElementById('emarsys-sdk')) {
        console.log('Emarsys SDK script already injected')
        return
      }

      if (!conf.sapAppCode || !conf.sapPublicKey || !conf.sapDomain) {
        console.warn('SAP SDK config is incomplete, skipping SDK injection')
        return
      }

      const js = document.createElement('script')
      js.id = 'emarsys-sdk'
      js.src = `https://client-version.cf.emarsys.net/web-emarsys-sdk-v4/latest/web-emarsys-sdk.js`
      const fs = document.getElementsByTagName('script')[0]
      fs && fs.parentNode && fs.parentNode.insertBefore(js, fs)
    } catch (e) {
      console.error('Failed to inject Emarsys sdk script', e)
    }

    window.WebEmarsysSdk = window.WebEmarsysSdk || []

    const config = {
      enableLogging: conf.sapDebug,
      applicationCode: conf.sapAppCode,
      enableMacSafariVapid: true,
      safariWebsitePushID: conf.sapDomain,
      defaultNotificationTitle: 'Where will you go next?',
      defaultNotificationIcon: `${conf.btAssets}/logo/airBaltic_logo_192x192.jpg`,
      autoSubscribe: false,
      serviceWorker: {
        url: 'service-worker.js',
        applicationServerPublicKey: conf.sapPublicKey
      }
    }

    window.WebEmarsysSdk.push(['init', config])

    window.WebEmarsysSdk.push(async () => {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const isSubscribed = await window.WebEmarsysSdk.isSubscribed()

        if (!isSubscribed) {
          await window.WebEmarsysSdk.subscribe()
        }
        return
      } else {
        console.log('Notifications are not granted yet')
      }
    })

    if (conf.sapDebug) {
      console.log('configuring Emarsys SDK with debug logging enabled', config)

      window.WebEmarsysSdk.push([
        'onSubscribe',
        (e) => {
          console.log('onSubscribe', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onUnsubscribe',
        (e) => {
          console.log('onUnsubscribe', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onPermissionPrompt',
        (e) => {
          console.log('onPermissionPrompt', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onPermissionGranted',
        (e) => {
          console.log('onPermissionGranted', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onPermissionDenied',
        (e) => {
          console.log('onPermissionDenied', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onSWInitError',
        (e) => {
          console.error('[Emarsys] onSWInitError', e)
        }
      ])
    }
  },
  init(name) {
    if (!store.hasModule(name)) {
      store.registerModule(name, FooterStore)
    }

    Init.initCookieAlert()
    Init.initBaseFooter()

    if (conf.storyblokEnabled) {
      Init.initStoryblokFooterItems(name)
      Init.initStoryblokFooterFaceliftLinks(name)
    } else {
      Init.initFooter()
      Init.initFooterFaceliftLinks(name)
    }
  },
  initBaseFooter() {
    if (store.state.main.pageType === 'index') {
      store.commit('footer/setShowScrollButton', false)
    }

    if (typeof subscribePopupData !== 'undefined' && subscribePopupData.length) {
      store.commit('footer/setSubscribePopupData', subscribePopupData[0])
    }

    if (typeof disableSubscribePopup !== 'undefined') {
      store.commit(`footer/setDisableSubscribePopup`, disableSubscribePopup)
    }
  },
  async initStoryblokFooterItems(name) {
    try {
      const language = store.state.main.selectedLang
      const footerLinks = await Storyblok.footerNavigation(language)

      store.commit(`${name}/setFooterItems`, footerLinks)
    } catch (error) {
      console.error('main-navigation/footer-navigation could not be loaded', error)
    }
  },
  async initStoryblokFooterFaceliftLinks(name) {
    const language = store.state.main.selectedLang
    const country = store.state.main.selectedCountry

    try {
      const {
        social,
        footerSemantics,
        footerAwards,
        contactUs,
        legalLinks,
        copyright,
        googlePrivacyPolicy,
        machineTranslationDisclaimer,
        paymentLogos
      } = await Storyblok.mainFooter(language, country)

      store.commit(`${name}/setFooterContactUs`, contactUs)
      store.commit(`${name}/setFooterLegalLinks`, legalLinks)
      store.commit(`${name}/setSocial`, social)
      store.commit(`${name}/setCopyright`, copyright)
      store.commit(`${name}/setSemantics`, footerSemantics)
      store.commit(`${name}/setAwards`, footerAwards)
      store.commit(`${name}/setGooglePrivacyPolicyText`, googlePrivacyPolicy)
      store.commit(
        `${name}/setMachineTranslationDisclaimer`,
        machineTranslationDisclaimer
      )
      store.commit(`${name}/setPaymentLogos`, paymentLogos)
    } catch (error) {
      console.error('main-navigation/main-footer could not be loaded', error)
    }
  },
  initCookieAlert() {
    if (typeof cookieContent !== 'undefined') {
      store.commit('main/setCookieAlertContent', cookieContent)

      const value = CookieUtils.getCookie('gdpr_settings')
      const split = value.split(',')
      const hasValue = ['fnc', 'sts', 'web', 'adv', '0', '1', '2', '3'].some((item) =>
        split.includes(item)
      )

      const hideAlert = value && hasValue
      store.commit('main/setHideCookieAlert', hideAlert)

      if (hasValue) {
        const presetMap = {
          '0': ['fnc', 'web', 'adv'],
          '1': ['fnc', 'web'],
          '2': ['fnc', 'adv'],
          '3': ['fnc']
        }

        const matchedPreset = Object.keys(presetMap).find((key) => split.includes(key))
        const cookieValue = matchedPreset ? presetMap[matchedPreset] : split

        store.dispatch('main/setGdprCookie', cookieValue)
        CookieUtils.setCookie('2e_send_GA', cookieValue.includes('sts'), 365)

        Init.InitSAP()
      }
    }

    if (
      store.state.main.pageType === 'privacypolicy-white' ||
      store.state.main.pageType === 'cookie_policy'
    ) {
      store.commit('main/setHideCookieAlert', true)
    }
  },
  initFooter() {
    if (typeof googlePrivacyPolicyText !== 'undefined') {
      store.commit('footer/setGooglePrivacyPolicyText', googlePrivacyPolicyText)
    }

    if (typeof footerSemantics !== 'undefined') {
      store.commit('footer/setSemantics', footerSemantics)
    }
  },
  initFooterFaceliftLinks(name) {
    axios
      .get(`/get-wide-footer-facelift-links/${store.state.main.selectedLang}/`)
      .then((response) => {
        store.commit(`${name}/setFooterLinks`, response.data)
      })
      .catch((e) => console.error('Error fetching footer links', e))
  }
}

export default Init
